import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ModalWrapper, SectionTitle } from '../../shared/components';
import { workloadApi } from '../tasks.api';
import type { EmployeeWorkload } from '../tasks.types';
import { errorMessage, fmtDate, fmtHours, fmtPct, isoDate, weekStart } from '../tasks.utils';
import { WorkloadChip } from './Chips';

/** One employee's workload, day by day over a week, with a link to their open tasks. */
export default function EmployeeWorkloadDialog({ employeeId, date, onClose }: { employeeId: number | null; date: string; onClose: () => void }) {
  const navigate = useNavigate();
  const [monday, setMonday] = useState(weekStart(date));
  const [w, setW] = useState<EmployeeWorkload | null>(null);

  useEffect(() => setMonday(weekStart(date)), [date, employeeId]);
  useEffect(() => {
    if (!employeeId) return;
    setW(null);
    workloadApi.employee(employeeId, monday, isoDate(dayjs(monday).add(6, 'day'))).then(setW).catch((e) => toast.error(errorMessage(e)));
  }, [employeeId, monday]);

  return (
    <ModalWrapper open={employeeId !== null} onClose={onClose} maxWidth='md'>
      <SectionTitle
        title={w ? `WORKLOAD · ${w.employeeName.toUpperCase()}` : 'WORKLOAD'}
        meta={w && <Typography variant='body2' color='text.secondary'>{w.departmentName}</Typography>}
        action={
          <Stack direction='row' alignItems='center'>
            <IconButton aria-label='Previous week' onClick={() => setMonday(isoDate(dayjs(monday).subtract(1, 'week')))}><Icon icon='mdi:chevron-left' /></IconButton>
            <Typography variant='body2'>{fmtDate(monday)}</Typography>
            <IconButton aria-label='Next week' onClick={() => setMonday(isoDate(dayjs(monday).add(1, 'week')))}><Icon icon='mdi:chevron-right' /></IconButton>
          </Stack>
        }
      />
      {!w && <LinearProgress />}
      {w && (
        <>
          <Typography variant='body2'>
            Active allocation {fmtHours(w.activeAllocationHours)} across {w.activeTasks} open task(s){w.overdueTasks ? `, ${w.overdueTasks} overdue` : ''}.
          </Typography>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Day</TableCell>
                <TableCell align='right'>Capacity</TableCell>
                <TableCell align='right'>Leave</TableCell>
                <TableCell align='right'>Allocated</TableCell>
                <TableCell align='right'>Actual</TableCell>
                <TableCell align='right'>Remaining</TableCell>
                <TableCell align='right'>Utilization</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {w.days.map((d) => (
                <TableRow key={d.date}>
                  <TableCell>{dayjs(d.date).format('ddd DD MMM')}</TableCell>
                  <TableCell align='right'>{fmtHours(d.capacityHours)}</TableCell>
                  <TableCell align='right'>{Number(d.leaveHours) ? fmtHours(d.leaveHours) : ''}</TableCell>
                  <TableCell align='right'>{fmtHours(d.allocatedHours)}</TableCell>
                  <TableCell align='right'>{fmtHours(d.actualHours)}</TableCell>
                  <TableCell align='right'>{fmtHours(d.remainingHours)}</TableCell>
                  <TableCell align='right'>{fmtPct(d.utilization)}</TableCell>
                  <TableCell><WorkloadChip status={d.workloadStatus} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
      <Stack direction='row' justifyContent='space-between'>
        <Button onClick={() => { onClose(); navigate(`/tasks?employeeId=${employeeId}`); }}>Open tasks</Button>
        <Button onClick={onClose}>Close</Button>
      </Stack>
    </ModalWrapper>
  );
}
